import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { scientificPartnerApi } from '@/features/workspace/api';

interface ProjectItem {
  code: string;
  kind: string;
  title: string;
  role_in_project: string;
  progress: number;
  next_step: string;
  deadline: string;
  status_label: string;
  badge_color: string;
  description?: string;
  coauthors?: string;
  files_count: number;
}

export const PartnerProjectsView: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [activeTab, setActiveTab] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<ProjectItem | null>(null);

  const loadProjects = async () => {
    try {
      const res = await scientificPartnerApi.module('projects', { tab: activeTab });
      if (res && Array.isArray(res.items)) {
        setProjects(res.items as ProjectItem[]);
      }
    } catch {
      setProjects([]);
    }
  };

  useEffect(() => {
    loadProjects();
  }, [activeTab]);

  const tabs = [
    { id: 'all', label: 'Все' },
    { id: 'grant', label: 'Гранты' },
    { id: 'article', label: 'Статьи' },
    { id: 'collab', label: 'Совместные' },
    { id: 'completed', label: 'Завершённые' },
  ];

  const getBadgeStyle = (color: string) => {
    switch (color) {
      case 'blue':
        return { bg: '#0284c7', text: '#ffffff' };
      case 'cyan':
        return { bg: '#06b6d4', text: '#0f172a' };
      case 'gray':
        return { bg: '#475569', text: '#f8fafc' };
      case 'teal':
      default:
        return { bg: '#0d9488', text: '#ffffff' };
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#0f172a',
          padding: '0.75rem 1.25rem',
          borderRadius: '10px',
          border: '1px solid #1e293b',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            type="button"
            onClick={() => navigate('/app/partner')}
            style={{
              padding: '0.4rem 0.85rem',
              backgroundColor: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '6px',
              color: '#f8fafc',
              fontSize: '0.8125rem',
              cursor: 'pointer',
            }}
          >
            ← Назад
          </button>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#f8fafc', margin: 0 }}>
              Мои проекты
            </h2>
            <span style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>
              Altyn Bakytzhanova · ZA000003
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate('/app/partner')}
          style={{
            padding: '0.4rem 0.85rem',
            backgroundColor: 'transparent',
            border: '1px solid #334155',
            borderRadius: '6px',
            color: '#94a3b8',
            fontSize: '0.8125rem',
            cursor: 'pointer',
          }}
        >
          Закрыть ✕
        </button>
      </div>

      <div
        style={{
          backgroundColor: '#0f172a',
          border: '1px solid #1e293b',
          borderRadius: '12px',
          padding: '1.5rem',
        }}
      >
        <div style={{ marginBottom: '1.25rem' }}>
          <span style={{ fontSize: '0.75rem', color: '#38bdf8', textTransform: 'uppercase', fontWeight: 600 }}>
            Исследовательские проекты
          </span>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#f8fafc', margin: '2px 0 0 0' }}>
            Мои проекты
          </h3>
          <p style={{ color: '#94a3b8', fontSize: '0.8125rem', margin: '4px 0 0 0' }}>
            Единый портфель грантовых проектов, рукописей, совместных исследований и завершённых научных результатов.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', overflowX: 'auto', paddingBottom: '4px' }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '0.45rem 1rem',
                backgroundColor: activeTab === tab.id ? '#0284c7' : '#1e293b',
                color: activeTab === tab.id ? '#ffffff' : '#cbd5e1',
                border: '1px solid #334155',
                borderRadius: '6px',
                fontSize: '0.8125rem',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: '1.25rem' }}>
          {projects.map((proj) => {
            const badge = getBadgeStyle(proj.badge_color);
            return (
              <div
                key={proj.code}
                style={{
                  backgroundColor: '#0b1120',
                  border: '1px solid #1e293b',
                  borderRadius: '10px',
                  padding: '1.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: '1rem',
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#94a3b8' }}>
                      {proj.code} · {proj.kind === 'GRANT' ? 'Грантовый проект' : proj.kind === 'ARTICLE' ? 'Статья / рукопись' : proj.kind === 'COLLAB' ? 'Совместное исследование' : 'Завершённый результат'}
                    </span>
                    <span
                      style={{
                        backgroundColor: badge.bg,
                        color: badge.text,
                        padding: '0.2rem 0.6rem',
                        borderRadius: '4px',
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                      }}
                    >
                      {proj.status_label}
                    </span>
                  </div>

                  <h4 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#f8fafc', margin: '0 0 0.25rem 0' }}>
                    {proj.title}
                  </h4>
                  <div style={{ fontSize: '0.8125rem', color: '#38bdf8', marginBottom: '1rem' }}>
                    {proj.role_in_project}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', backgroundColor: '#0f172a', padding: '0.75rem', borderRadius: '8px', border: '1px solid #1e293b' }}>
                    <div>
                      <div style={{ fontSize: '0.6875rem', color: '#94a3b8', textTransform: 'uppercase' }}>Готовность</div>
                      <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#f8fafc' }}>{proj.progress}%</div>
                      <div style={{ height: '4px', backgroundColor: '#1e293b', borderRadius: '2px', marginTop: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${proj.progress}%`, height: '100%', backgroundColor: '#38bdf8' }} />
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.6875rem', color: '#94a3b8', textTransform: 'uppercase' }}>Ближайший шаг</div>
                      <div style={{ fontSize: '0.75rem', color: '#cbd5e1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {proj.next_step}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.6875rem', color: '#94a3b8', textTransform: 'uppercase' }}>Срок / результат</div>
                      <div style={{ fontSize: '0.75rem', color: '#cbd5e1' }}>{proj.deadline}</div>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '0.75rem', borderTop: '1px solid #1e293b' }}>
                  <button
                    type="button"
                    onClick={() => setSelectedProject(proj)}
                    style={{
                      padding: '0.45rem 1rem',
                      backgroundColor: '#1e293b',
                      border: '1px solid #38bdf8',
                      borderRadius: '6px',
                      color: '#38bdf8',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Открыть workspace
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedProject && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
          }}
        >
          <div
            style={{
              backgroundColor: '#0f172a',
              border: '1px solid #334155',
              borderRadius: '12px',
              padding: '1.75rem',
              maxWidth: '640px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '1.25rem',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontSize: '0.75rem', color: '#38bdf8', fontWeight: 700 }}>
                  {selectedProject.code} · {selectedProject.status_label}
                </span>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#f8fafc', margin: '2px 0 0 0' }}>
                  {selectedProject.title}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedProject(null)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.25rem', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem', color: '#cbd5e1' }}>
              <div><strong>Роль в проекте:</strong> {selectedProject.role_in_project}</div>
              <div><strong>Описание:</strong> {selectedProject.description || 'Научный проект платформы.'}</div>
              <div><strong>Соавторы и участники:</strong> {selectedProject.coauthors || 'Syrym Zhanzhigitov'}</div>
              <div><strong>Ближайший шаг:</strong> {selectedProject.next_step}</div>
              <div><strong>Срок / Дедлайн:</strong> {selectedProject.deadline}</div>
              <div><strong>Готовность:</strong> {selectedProject.progress}%</div>
              <div><strong>Связанных документов:</strong> {selectedProject.files_count} файла(ов)</div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
              <button
                type="button"
                onClick={() => {
                  setSelectedProject(null);
                  navigate('/app/partner/academic_connections');
                }}
                style={{
                  padding: '0.5rem 1.25rem',
                  backgroundColor: '#0284c7',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Обсудить в связях
              </button>
              <button
                type="button"
                onClick={() => setSelectedProject(null)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  color: '#cbd5e1',
                  fontSize: '0.8125rem',
                  cursor: 'pointer',
                }}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
